import origChartConnection from "@assets/redesign/icons/chartConnection.svg";
import origDrop from "@assets/redesign/icons/drop.svg";
import {
  SHOW_ELECTRIC_SCHEME_TABS,
  SHOW_ELECTRIC_SCHEME_TOOLBAR
} from '@components/../config';
import { SocketContext } from '@components/Context/SocketContext';
import CustomSelect from '@components/global/CustomSelect/CustomSelect';
import Icon from "@components/global/new/Icon/Icon";
import { MINER_STATUSES_E } from '@constants/MinerStatuses';
import { useDragWatch3 } from "@mobx/hooks/useDragWatch3";
import { useEditorState3 } from "@mobx/hooks/useEditorState3";
import { usePlaceEditorsState3 } from "@mobx/hooks/usePlacedEditorState3";
import usePlaceID from "@mobx/hooks/usePlaceID";
import { useSchemeDragTarget3 } from "@mobx/hooks/useSchemeDragTarget3";
import connectionsStore, {
  BuildingData3, ConsumerKind3, DEFAULT_KIND3, editorNameFromID3,
  isBuildingData3, isRackSectionData3, isSignalAllowed3, NodeSignal3, PlaceStore
} from "@mobx/store/connections";
import {
  IRack,
  IRackItem3, isIRack, isMinerItem3, toRackItem3
} from "@mobx/store/locations/types";
import { SocketSubTypes } from '@services/http/socket/requests';
import {
  MinerHashEvent, SocketEventTypes,
  SocketMessage
} from '@services/http/socket/responses';
import { MinerControlRequestTyped } from '@services/minerContol';
import { EQUIPMENT_TYPE } from "@store/Equipments/actionTypes";
import { useGlobalNotifs } from '@utils/hooks/useGlobalNotifs';
import useTypedRequest from '@utils/hooks/useTypedRequest';
import { makeNotifs } from '@utils/makeNotifs';
import { Button, Collapse, Tabs } from 'antd';
import cn from "classnames";
import { observer } from "mobx-react-lite";
import React, {
  useContext, useEffect,
  useMemo, useRef, useState
} from 'react';
import { useIntl } from 'react-intl';
import { ReactFlowProvider, Viewport } from 'reactflow';
import { WLP202603 } from "../../../config";
import { Dragger3 } from "./dragger";
import styles from './index.module.scss';
import NetworkEditor from './NetworkEditor';
import { timePassedSinceControl3 } from './nodes';
import wlpChartConnection from "@components/WLP202603/assets/redesign/icons/chartConnection.svg";
import wlpDrop from "@components/WLP202603/assets/redesign/icons/drop.svg";

const chartConnection = WLP202603 ? wlpChartConnection : origChartConnection;
const drop = WLP202603 ? wlpDrop : origDrop;

export interface ConnectionsEditorProps3 { }

const toFlowDropPoint3 = (
  point?: { x: number; y: number },
  editor?: HTMLDivElement | null,
  viewport?: Viewport
) => {
  if (!point) return undefined;
  if (!editor) return point;

  const rect = editor.getBoundingClientRect();
  const localPoint = {
    x: point.x - rect.left,
    y: point.y - rect.top,
  };

  if (!viewport) return localPoint;

  return {
    x: (localPoint.x - viewport.x) / viewport.zoom,
    y: (localPoint.y - viewport.y) / viewport.zoom,
  };
};

const useControl = (_: ConnectionsEditorProps3) => {
  const { ok: notifs } = makeNotifs();
  const { onSuccess } = useGlobalNotifs();
  const [collapsed, setCollapsed] = useState(true);
  const [activeTab, setActiveTab] = useState<EQUIPMENT_TYPE>(DEFAULT_KIND3);
  const [focusNodeID, setFocusNodeID] = useState<string>();
  const [highlightNodeIDs, setHighlightNodeIDs] = useState<string[]>([]);

  const toggleCollapse = () => setCollapsed(!collapsed);
  const switchTab = (tab: EQUIPMENT_TYPE) => setActiveTab(tab);

  const { placeID } = usePlaceID(true);
  const [placeStore, setPlaceStore] = useState<PlaceStore>();

  const { data: dataToSave, initialized } = usePlaceEditorsState3(placeID) ?? {};
  const { data: electricEditorData, toTurnOn } = useEditorState3(EQUIPMENT_TYPE.ELECTRICAL, placeID) ?? {};
  const activeViewport = useEditorState3(activeTab, placeID, (editor) => editor?.viewport);

  useEffect(() => {
    if (placeID !== undefined) {
      setPlaceStore(connectionsStore.getPlaceStore(placeID));
      return () => {
        connectionsStore.removePlaceStore(placeID);
        setPlaceStore(undefined);
      }
    }
  }, [placeID])

  const consumerNodes = useMemo(() => electricEditorData?.nodes.filter(n => n.data.type === "consumer"), [electricEditorData]);
  const activeNodes = useMemo(() =>
    consumerNodes
      ?.filter(n => n.data.lastSignal?.active)
      .sort((a, b) => a.id.localeCompare(b.id)),
    [consumerNodes]
  );
  const activeNodesSign = useMemo(() => activeNodes?.map(n => n.id).join(" ") ?? "", [activeNodes]);
  const miners = useMemo(() => (electricEditorData?.nodes
    .filter(n => n.data.type === "consumer")
    .map(n => {
      switch (n.data.consumer) {
        case "section":
          return isRackSectionData3(n.data.userData) && n.data.userData.miner
            ? n.data.userData.miner.miner_id
            : undefined;
        case "miner":
          return (n.data.userData as IRackItem3).miner_id;
        case "rack":
          return (n.data.userData as IRack).rack_items.filter(isMinerItem3).map(item => item.miner_id);
        case "building":
          return (n.data.userData as BuildingData3).miners.filter(isMinerItem3).map(item => item.miner_id);
      }
    })
    .filter(n => n !== undefined) as undefined | (number | number[])[])?.flat()
    , [electricEditorData]);
  const subscribedMiners = useRef<number[]>();

  const dataRef = useRef(dataToSave);

  const elementRef = useRef<HTMLDivElement | null>(null);
  const dragCtrl = useDragWatch3(elementRef, placeID);
  const toggleButtonRef = useRef<HTMLDivElement | null>(null);
  const networkTabRef = useRef<HTMLButtonElement | null>(null);
  const electricTabRef = useRef<HTMLButtonElement | null>(null);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const {
    addMessageListener,
    removeMessageListener,
    subscribe,
    unsubscribe
  } = useContext(SocketContext);

  const { request: controlMiner, loading } = useTypedRequest(MinerControlRequestTyped);

  const onMinerHashRate = (message: SocketMessage<any>) => {
    placeStore?.changeMinerState(message.event as MinerHashEvent);
  }

  useSchemeDragTarget3(toggleButtonRef, placeID, (lst) => lst.length && setCollapsed(false));
  useSchemeDragTarget3(networkTabRef, placeID, (lst) => lst.length && switchTab(EQUIPMENT_TYPE.NETWORK));
  useSchemeDragTarget3(electricTabRef, placeID, (lst) => lst.length && switchTab(EQUIPMENT_TYPE.ELECTRICAL));
  useSchemeDragTarget3(editorRef, placeID, undefined, (els, point) => {
    //dbg.log("KIND", els);
    const my = els.filter(el => new Set<ConsumerKind3>(["building", "rack", "miner", "section"]).has(el.kind));
    const dropPoint = toFlowDropPoint3(point, editorRef.current, activeViewport);
    if (!my.length) return;

    const dropMeta = placeStore?.drop(undefined, els, dropPoint ?? { x: 100, y: 100 });
    if (!dropMeta?.duplicateNodeIDs.length) return;

    switchTab(EQUIPMENT_TYPE.ELECTRICAL);
    setFocusNodeID(dropMeta.duplicateNodeIDs[0]);
    setHighlightNodeIDs(dropMeta.duplicateNodeIDs);
  });

  useEffect(() => {
    const cb = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") placeStore?.clearDrag();
    };
    document.addEventListener("keydown", cb);
    return () => document.removeEventListener("keydown", cb);
  }, []);

  useEffect(() => {
    addMessageListener(SocketEventTypes.MINER_HASRATE, onMinerHashRate);
    return () => removeMessageListener(SocketEventTypes.MINER_HASRATE, onMinerHashRate);
  }, []);

  useEffect(() => {
    //dbg.log("MINERS", miners);
    if (miners?.length) {
      subscribe(SocketSubTypes.Miner, miners);
      subscribedMiners.current = [...miners];
    }
    return () => {
      if (subscribedMiners.current?.length) {
        unsubscribe(SocketSubTypes.Miner, subscribedMiners.current);
      }
    }
  }, [miners?.sort().join(",")]);

  useEffect(() => {
    if (placeID === undefined || initialized) return;
    connectionsStore.getPlaceStore(placeID)?.init();
  }, [initialized, placeID, activeTab]);

  useEffect(() => {
    //dbg.log("ACTIVE NODES", activeNodesSign);

    if (!activeNodes?.length) return;

    const miners = {
      [NodeSignal3.START]: [] as IRackItem3[],
      [NodeSignal3.STOP]: [] as IRackItem3[],
      [NodeSignal3.RESET]: [] as IRackItem3[],
    };

    const controlledNodes = {
      [NodeSignal3.START]: new Set<string>,
      [NodeSignal3.STOP]: new Set<string>,
      [NodeSignal3.RESET]: new Set<string>,
    };

    for (const n of activeNodes) {
      const signal = n.data.lastSignal!.signal;
      const passed = timePassedSinceControl3(n.data.lastSignal?.ts) ?? 0;
      if (passed > 400) {
        //dbg.log("FAIL 1");
        placeStore?.reportMinersSignal(signal, new Set([n.id]), false);
        continue;
      }
      const idata = n.data.userData;

      if (!isSignalAllowed3(n.data.state ?? MINER_STATUSES_E.OFF, signal)) {
        //dbg.log("FAIL 2", n.id, n.data.state, signal, isSignalAllowed(n.data.state ?? MINER_STATUSES_E.OFF, signal));
        placeStore?.reportMinersSignal(signal, new Set([n.id]), false);
        continue;
      }

      controlledNodes[signal].add(n.id);
      if (isRackSectionData3(idata)) {
        if (idata.miner && isSignalAllowed3(idata.miner.status, signal)) {
          miners[signal].push(idata.miner);
        }
      } else if (isMinerItem3(idata)) {
        miners[signal].push(toRackItem3(idata));
      } else if (isIRack(idata)) {
        for (const m of idata.rack_items) {
          if (isMinerItem3(m) && isSignalAllowed3(m.status, signal)) miners[signal].push(toRackItem3(m));
        }
      } else if (isBuildingData3(idata)) {
        for (const m of idata.miners) {
          if (isMinerItem3(m) && isSignalAllowed3(m.status, signal)) miners[signal].push(m);
        }
      }
    }

    const run = async (signal: NodeSignal3, miners: IRackItem3[], controlledNodes: Set<string>) => {
      const ok = await placeStore?.confirmAndMinersControl(signal, miners, controlledNodes);
      if (ok) onSuccess(notifs.success);
    }

    (async () => {
      for (const signal of [NodeSignal3.START, NodeSignal3.STOP, NodeSignal3.RESET]) {
        if (miners[signal].length) await run(signal, miners[signal], controlledNodes[signal])
      }
    })();

  }, [activeNodesSign])

  useEffect(() => {
    if (!toTurnOn?.miners.length) return;
    placeStore?.minersInRackControl(NodeSignal3.START, toTurnOn.miners, toTurnOn.nodes);
  }, [toTurnOn])

  useEffect(() => {
    dataRef.current = dataToSave;
    if (!initialized || !dataToSave || dataToSave.saved) return;
    const timeoutId = setTimeout(() => {
      if (dataRef.current && !dataRef.current.saved) placeStore?.save(dataRef.current);
    }, 3 * 1000);
    return () => clearTimeout(timeoutId);
  }, [dataToSave, initialized, placeStore]);

  useEffect(() => {
    if (!highlightNodeIDs.length) return;
    const timeoutId = window.setTimeout(() => setHighlightNodeIDs([]), 1800);
    return () => window.clearTimeout(timeoutId);
  }, [highlightNodeIDs]);

  useEffect(() => { placeStore?.setCurrentKind(activeTab) }, [activeTab]);

  return {
    placeID,
    data: dataToSave,
    initialized,
    savedRef: dataRef,
    placeStore,
    collapsed,
    activeTab,
    dragCtrl,
    elementRef,
    toggleButtonRef,
    networkTabRef,
    electricTabRef,
    editorRef,
    loading,
    focusNodeID,
    highlightNodeIDs,
    clearFocusNode() {
      setFocusNodeID(undefined);
    },
    toggleCollapse,
    switchTab,
  }
}

const ConnectionsEditor3: React.FC<ConnectionsEditorProps3> = observer((props) => {
  const ctrl = useControl(props);
  const { formatMessage } = useIntl();

  return (
    <ReactFlowProvider>
      <div className={cn(styles.connectionDiagram, { [styles.inactive]: ctrl.loading })} ref={ctrl.elementRef}>
        {ctrl.dragCtrl?.isDrag && <Dragger3 ctrl={ctrl.dragCtrl} placeID={ctrl.placeID} />}
        <Collapse
          className={styles.collapse}
          ghost
          activeKey={ctrl.collapsed ? "" : "1"}
          items={[
            {
              key: "1",
              label: <div className={styles.header} ref={ctrl.toggleButtonRef} onClick={ctrl.toggleCollapse}>
                <h4 className={styles.title}>
                  {formatMessage({ id: "location.diagram.title" })}
                </h4>
                <Icon
                  className={styles.chartIcon}
                  src={chartConnection}
                  noHover
                />
                <Icon
                  className={cn(styles.collapseButton, { [styles.collapsed]: ctrl.collapsed })}
                  src={drop}
                />
              </div>,
              children: <>
                {SHOW_ELECTRIC_SCHEME_TABS && <Tabs
                  activeKey={ctrl.activeTab}
                  onTabClick={key => ctrl.switchTab(key as EQUIPMENT_TYPE)}
                  items={[
                    {
                      label: <span ref={ctrl.electricTabRef}>{formatMessage({ id: "location.available_equipment.electrical" })}</span>,
                      key: EQUIPMENT_TYPE.ELECTRICAL,
                    },
                    {
                      label: <span ref={ctrl.networkTabRef}>{formatMessage({ id: "location.available_equipment.network" })}</span>,
                      key: EQUIPMENT_TYPE.NETWORK,
                    },
                  ]}
                />}
                <div className={styles.electricalTab}>
                  {SHOW_ELECTRIC_SCHEME_TOOLBAR &&
                    <div className={styles.controlsContainer}>
                      <label className={styles.schemeSelectorLabel}>Select scheme</label>
                      <div className={styles.controls}>
                        <CustomSelect
                          className={styles.selectScheme}
                          options={[{ value: 1, label: "Example Scheme" }]}
                          selected={1}
                          onChange={() => { }}
                        />
                        <span>or create a new one</span>
                        <Button>+ Create a new scheme</Button>
                      </div>
                    </div>
                  }
                  {
                    ctrl.placeID !== undefined &&
                    <NetworkEditor
                      placeID={ctrl.placeID}
                      kind={ctrl.activeTab}
                      key={editorNameFromID3(ctrl.activeTab, ctrl.placeID)}
                      editorRef={ctrl.editorRef}
                      schemeDragActive={!!ctrl.dragCtrl?.isDrag}
                      focusNodeID={ctrl.activeTab === EQUIPMENT_TYPE.ELECTRICAL ? ctrl.focusNodeID : undefined}
                      highlightNodeIDs={ctrl.activeTab === EQUIPMENT_TYPE.ELECTRICAL ? ctrl.highlightNodeIDs : undefined}
                      onFocusHandled={ctrl.clearFocusNode}
                    />
                  }
                </div>
              </>,
              showArrow: false,
            },
          ]}
        />
      </div>
    </ReactFlowProvider>
  );
});

ConnectionsEditor3.displayName = "ConnectionsEditor3";

export default ConnectionsEditor3;
